import i18n from '@/utils/i18n';
import { formatCurrency, getCurrencySymbol } from '@/utils/money';
import { OrderEntrySplitMetricsData } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/loaders/use-sales-portal-order-summary-loader';
import { useConfidentialVisibilityStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/order-entry-confidential-visibility-store';
import { useMemo } from 'react';

export interface OrderSummaryMetricRow {
  /** Stable identifier used as a React list key (e.g. `'total'`, `'gp_percentage'`). */
  key: string;
  /** Translated display label for the row (e.g. "GP %", "Total"). */
  label: string;
  /** Formatted display value (e.g. "25.5%", "$3.50", "4"). */
  value: string;
}

interface UseOrderSummaryMetricRowsOptions {
  /** When true, GP rows are excluded regardless of the confidential toggle */
  isGrossProfitHidden?: boolean;
}

/**
 * Builds the list of summary-metric rows rendered above the unit/subtotal
 * footer in each checkout-panel order summary card.
 *
 * GP% and GP Amount rows are excluded when the sales-rep confidential-field
 * toggle is off, or when `isGrossProfitHidden` is set to control visibility
 *
 * @param splitOrderMetrics - One entry per fulfillment split from the order
 *   summary loader. Index-aligned with the returned row arrays.
 * @param currency - ISO currency code used to format the GP Amount value
 *   (e.g. `'USD'`).
 * @param options - Optional configuration
 * @param options.isGrossProfitHidden - When true, GP rows are excluded
 *   regardless of the confidential toggle (e.g. when the supplier disables
 *   gross profit display via dynamic config).
 * @returns One `OrderSummaryMetricRow[]` per split, already filtered for
 *   confidential visibility.
 *
 * @example
 * ```ts
 * const splitOrderMetricRows = useOrderSummaryMetricRows(
 *   orderEntrySummary?.splitOrderMetrics ?? [],
 *   currency,
 * );
 * ```
 */
export const useOrderSummaryMetricRows = (
  splitOrderMetrics: OrderEntrySplitMetricsData[],
  currency: string,
  options?: UseOrderSummaryMetricRowsOptions,
): OrderSummaryMetricRow[][] => {
  const isGrossProfitHidden = options?.isGrossProfitHidden;
  const showConfidentialFields = useConfidentialVisibilityStore(
    (state) => state.showConfidentialFields,
  );

  return useMemo(
    () =>
      splitOrderMetrics.map((metrics) => {
        const rows: OrderSummaryMetricRow[] = [
          {
            key: 'total',
            label: i18n.t('total'),
            value: String(metrics.itemCount),
          },
        ];

        if (!isGrossProfitHidden && showConfidentialFields) {
          rows.push(
            {
              key: 'gp_percentage',
              label: i18n.t(
                'sales_portal.order_entry.order_summary_bottom_bar.metric.gp_percentage.title',
              ),
              value: metrics.grossProfitPercentageDisplayString || '0%',
            },
            {
              key: 'gp_amount',
              label: `${i18n.t(
                'sales_portal.order_entry.order_summary_bottom_bar.metric.gp_amount.title',
              )} ${getCurrencySymbol(currency)}`,
              value: formatCurrency(metrics.grossProfitInMicros ?? 0, currency),
            },
          );
        }

        return rows;
      }),
    [splitOrderMetrics, currency, isGrossProfitHidden, showConfidentialFields],
  );
};
